
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { SweepstakesEntry } from '@/hooks/useSweepstakesData';

interface EntriesTableProps {
  entries: SweepstakesEntry[];
  loading: boolean;
  sweepstakesId: string | null;
}

const EntriesTable: React.FC<EntriesTableProps> = ({ 
  entries, 
  loading,
  sweepstakesId
}) => {
  const { t } = useTranslationUtils();
  
  if (!sweepstakesId) {
    return null;
  }
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.sweepstakesEntries', 'Sweepstakes Entries')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.sweepstakesEntries', 'Sweepstakes Entries')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center p-4">
            <p className="text-muted-foreground">{t('admin.noEntries', 'No entries found for this sweepstakes')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('admin.sweepstakesEntries', 'Sweepstakes Entries')} 
          <Badge className="ml-2" variant="outline">
            {entries.length} {entries.length === 1 ? t('admin.entry', 'entry') : t('admin.entries', 'entries')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.user', 'User')}</TableHead>
                <TableHead>{t('admin.tier', 'Tier')}</TableHead>
                <TableHead>{t('admin.points', 'Points')}</TableHead>
                <TableHead>{t('admin.entryDate', 'Entry Date')}</TableHead>
                <TableHead>{t('admin.entrySource', 'Source')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {entry.customer_profiles?.first_name} {entry.customer_profiles?.last_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.customer_profiles?.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {entry.customer_profiles?.tier || 'standard'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {entry.customer_profiles?.points_balance || 0}
                  </TableCell>
                  <TableCell>
                    {format(new Date(entry.entry_date), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {entry.entry_source || 'automatic'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default EntriesTable;
